/**
 * The step-by-step market picker: Game (search + league pills, sectioned by start time) → Market →
 * Participant (props / per-participant 3-way) → Line, one at a time under a tappable breadcrumb.
 * A step with exactly one choice is skipped (and gets no crumb); the logic is lib/marketMaking.ts
 * `resolveQuoteFlow` over `quotableMarkets`.
 *
 * Shared by Market making's quote builder (components/marketMaking/QuoteBuilder.tsx, which renders
 * its quote desk once the flow reaches "quote"); `MarketPicker` is the standalone version that
 * hands the picked book back instead (the Market screen no longer uses it: it's reached from the
 * game page).
 *
 *   useMarketPick   the pick state + the game's book + the resolved flow
 *   PickerCrumbs    "Warriors @ Timberwolves › Spread › −14.5"
 *   PickerSteps     the current step's card (null once the flow reaches "quote")
 *   MarketPicker    all three, with a title and ✕, calling `onPick` when a line is chosen
 */
import { useEffect, useMemo, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import type { Contest } from "@greenbook/client";
import {
  backTo,
  gameSections,
  leagueCounts,
  marketHint,
  orderMarkets,
  quotableGames,
  quotableMarkets,
  resolveQuoteFlow,
  shortGame,
  shortSide,
  type MmBook,
  type MmLineOption,
  type MmMarket,
  type MmParticipant,
  type QuoteFlow,
  type QuotePick,
  type QuoteStep,
} from "../lib/marketMaking.js";
import { fmtKickoff, fmtPrice } from "../lib/format.js";
import { useOddsFormat } from "../lib/odds.js";
import { Pill, SectionHeading, SoftCard, Skeleton } from "./kit.js";
import { ErrorState } from "./ui.js";
import { gameLabel, type MarketMakingSource } from "./marketMaking/shared.js";

/** What the picker reads (a subset of the Market making source; the Market screen supplies the same hooks). */
export type PickerSource = Pick<MarketMakingSource, "useGames" | "useContest" | "useLeagues" | "useGameBook">;

export const EMPTY_PICK: QuotePick = { contestId: null, marketKey: null, participantId: null, bookKey: null };

export interface MarketPickState {
  pick: QuotePick;
  setPick: Dispatch<SetStateAction<QuotePick>>;
  /** Back to a step (clears it and everything after it). */
  go: (step: QuoteStep) => void;
  contest: Contest | undefined;
  gb: ReturnType<PickerSource["useGameBook"]>;
  /** The game's two-sided markets in list order; null while its book loads. */
  markets: MmMarket[] | null;
  flow: QuoteFlow;
}

/** The pick and everything resolved from it. `init` is read once. */
export function useMarketPick(source: PickerSource, init: () => QuotePick): MarketPickState {
  const [pick, setPick] = useState<QuotePick>(init);
  const contest = source.useContest(pick.contestId ?? undefined);
  const gb = source.useGameBook(pick.contestId ?? undefined);
  const markets = useMemo(
    () => (gb.positions && gb.markets && !gb.isLoading ? orderMarkets(quotableMarkets(gb.positions, gb.markets)) : null),
    [gb.positions, gb.markets, gb.isLoading],
  );
  const flow = useMemo(() => resolveQuoteFlow(markets, pick), [markets, pick]);
  // Hold on to auto-picked choices, so a line or market appearing on a stream update doesn't pull
  // the pick back to a step.
  useEffect(() => {
    const m = flow.market?.marketKey ?? null;
    const pid = flow.participant?.id ?? null;
    const b = flow.book?.key ?? null;
    if ((m != null && pick.marketKey == null) || (pid != null && pick.participantId == null) || (b != null && pick.bookKey == null)) {
      setPick((p) => ({ ...p, marketKey: p.marketKey ?? m, participantId: p.participantId ?? pid, bookKey: p.bookKey ?? b }));
    }
  }, [flow, pick]);
  const go = (step: QuoteStep) => setPick((p) => backTo(p, step));
  return { pick, setPick, go, contest, gb, markets, flow };
}

/* ================================================================ breadcrumb */

export interface Crumb {
  key: QuoteStep;
  label: string;
  /** Back to this step; absent = fixed (a re-quote's game). */
  onClick?: () => void;
}

/** The crumbs for a pick: game, then each chosen step that had more than one choice. */
export function pickCrumbs(s: MarketPickState, game: string, opts: { fixedGame?: boolean } = {}): Crumb[] {
  const { pick, flow, go } = s;
  const crumbs: Crumb[] = [];
  if (!pick.contestId) return crumbs;
  crumbs.push({ key: "game", label: game ? shortGame(game) : "…", onClick: opts.fixedGame ? undefined : () => go("game") });
  if (flow.market && flow.shown.includes("market")) crumbs.push({ key: "market", label: flow.market.label, onClick: () => go("market") });
  if (flow.participant && flow.shown.includes("participant")) crumbs.push({ key: "participant", label: flow.participant.name, onClick: () => go("participant") });
  if (flow.book && flow.shown.includes("line")) {
    const line = flow.lines.find((l) => l.book.key === flow.book!.key);
    crumbs.push({ key: "line", label: line?.label ?? flow.book.label, onClick: () => go("line") });
  }
  return crumbs;
}

/** "Warriors @ Timberwolves › Spread › −14.5": each crumb goes back to its step. */
export function PickerCrumbs({ crumbs, kickoff, label = "Quote steps" }: { crumbs: Crumb[]; kickoff: string | null; label?: string }) {
  if (crumbs.length === 0) return null;
  return (
    <nav aria-label={label} className="-my-1 flex min-w-0 flex-wrap items-center gap-x-0.5 text-[13px]">
      {crumbs.map((c, i) => (
        <span key={c.key} className="flex min-w-0 items-center gap-x-0.5">
          {i > 0 && (
            <span className="px-0.5 text-faint" aria-hidden>
              ›
            </span>
          )}
          {c.onClick ? (
            <button
              type="button"
              onClick={c.onClick}
              title={`Change ${c.key === "line" ? "line" : c.key}`}
              className={`min-h-11 min-w-0 truncate rounded px-1 underline-offset-2 hover:underline lg:min-h-8 ${i === crumbs.length - 1 ? "font-medium text-ink" : "text-dim hover:text-ink"}`}
            >
              {c.label}
            </button>
          ) : (
            <span className={`flex min-h-11 min-w-0 items-center truncate px-1 lg:min-h-8 ${i === crumbs.length - 1 ? "font-medium text-ink" : "text-dim"}`}>{c.label}</span>
          )}
        </span>
      ))}
      {kickoff && <span className="nums ml-auto pl-2 text-[11px] text-faint">{fmtKickoff(kickoff)}</span>}
    </nav>
  );
}

/* ===================================================================== steps */

/**
 * The current step's card for a pick: Game, Market, Participant or Line (with its loading, empty
 * and error states). Null once the flow reaches "quote". `note` explains a pick that went away.
 */
export function PickerSteps({ source, state, note = null }: { source: PickerSource; state: MarketPickState; note?: string | null }) {
  const { flow, gb, markets, setPick } = state;
  if (flow.step === "game") return <GameStep source={source} onPick={(c) => setPick({ ...EMPTY_PICK, contestId: c.id })} />;
  if (gb.isError) return <ErrorState error={gb.error} onRetry={gb.refetch} />;
  if (flow.step === "market") {
    if (markets == null)
      return (
        <StepCard title="Market">
          <RowsSkeleton />
        </StepCard>
      );
    if (markets.length === 0)
      return (
        <StepCard title="Market">
          <div className="py-4 text-center text-[13px] text-dim">No two-sided market with prices right now</div>
        </StepCard>
      );
    return <MarketStep markets={markets} note={note} onPick={(m) => setPick((p) => ({ ...p, marketKey: m.marketKey, participantId: null, bookKey: null }))} />;
  }
  if (flow.step === "participant" && flow.market)
    return <ParticipantStep market={flow.market} participants={flow.participants} note={note} onPick={(x) => setPick((p) => ({ ...p, participantId: x.id, bookKey: null }))} />;
  if (flow.step === "line") return <LineStep lines={flow.lines} note={note} onPick={(l) => setPick((p) => ({ ...p, bookKey: l.book.key }))} />;
  return null;
}

const ROW =
  "flex min-h-11 w-full min-w-0 items-center gap-2 px-3 py-1.5 text-left transition-colors duration-150 hover:bg-raised motion-reduce:transition-none";
const LIST = "flex min-w-0 flex-col divide-y divide-border/50 overflow-hidden rounded-md border border-border/70 bg-bg";
/** Show a filter box above a list longer than this. */
const FILTER_OVER = 8;

function StepCard({ title, right, children }: { title: string; right?: ReactNode; children: ReactNode }) {
  return (
    <SoftCard className="flex min-w-0 flex-col gap-2 p-3">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-dim">{title}</span>
        {right}
      </div>
      {children}
    </SoftCard>
  );
}

function RowsSkeleton({ n = 5 }: { n?: number }) {
  return (
    <div className="flex flex-col gap-1.5">
      {Array.from({ length: n }, (_, i) => (
        <Skeleton key={i} className="h-11 w-full" />
      ))}
    </div>
  );
}

function FilterInput({ value, onChange, placeholder, label }: { value: string; onChange: (v: string) => void; placeholder: string; label: string }) {
  return (
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={label}
      className="min-h-11 w-full rounded-md border border-border-2 bg-bg px-3 text-[14px] text-ink outline-none placeholder:text-faint focus:border-amber/60 lg:min-h-9"
    />
  );
}

const matches = (text: string, q: string): boolean => text.toLowerCase().includes(q.trim().toLowerCase());

function Note({ children }: { children: ReactNode }) {
  return <p className="text-[12px] text-amber">{children}</p>;
}

/* ---------------------------------------------------------------- 1. game */

function GameStep({ source, onPick }: { source: PickerSource; onPick: (c: Contest) => void }) {
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [league, setLeague] = useState<string | null>(null);
  useEffect(() => {
    const t = setTimeout(() => setQ(text.trim()), 300);
    return () => clearTimeout(t);
  }, [text]);
  const games = source.useGames(q);
  const leagueOf = source.useLeagues();
  const list = useMemo(() => quotableGames(games.items), [games.items]);
  const leagues = useMemo(() => leagueCounts(list, (id) => leagueOf(id)?.name), [list, leagueOf]);
  const active = league && leagues.some((l) => l.id === league) ? league : null;
  const sections = useMemo(() => gameSections(list, active, Date.now()), [list, active]);

  return (
    <StepCard title="Game">
      <FilterInput value={text} onChange={setText} placeholder="Search games (e.g. hawks)" label="Search games" />
      {leagues.length > 1 && (
        <div className="-mx-3 flex min-w-0 items-center gap-1.5 overflow-x-auto px-3" role="toolbar" aria-label="Leagues">
          <Pill active={active == null} onClick={() => setLeague(null)} count={list.length}>
            All
          </Pill>
          {leagues.map((l) => (
            <Pill key={l.id} active={active === l.id} onClick={() => setLeague(l.id)} count={l.n}>
              {l.name || "Other"}
            </Pill>
          ))}
        </div>
      )}
      {games.isError ? (
        <ErrorState error={games.error} onRetry={games.refetch} />
      ) : games.isLoading ? (
        <RowsSkeleton />
      ) : sections.length === 0 ? (
        <div className="py-4 text-center text-[13px] text-dim">{q ? `No open games match “${q}”` : "No open games right now"}</div>
      ) : (
        <div className="flex min-w-0 flex-col">
          {sections.map((s) => (
            <section key={s.key} aria-label={s.label} className="flex min-w-0 flex-col">
              <SectionHeading title={s.label} count={s.contests.length} tone={s.key === "live" ? "live" : "default"} className="!pt-2 first:!pt-0" />
              <div className={LIST}>
                {s.contests.map((c) => {
                  const lg = active == null && c.league_id ? leagueOf(c.league_id)?.name : undefined;
                  const when = s.key === "live" ? "In progress" : s.key === "soon" || s.key === "today" ? (fmtKickoff(c.starts_at).split(", ")[1] ?? fmtKickoff(c.starts_at)) : fmtKickoff(c.starts_at);
                  return (
                    <button key={c.id} type="button" onClick={() => onPick(c)} className={ROW}>
                      <span className="min-w-0 flex-1 truncate text-[13px] text-ink" title={gameLabel(c)}>
                        {shortGame(gameLabel(c))}
                      </span>
                      {lg && <span className="shrink-0 rounded bg-panel-2 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-dim">{lg}</span>}
                      <span className={`nums w-[5.5rem] shrink-0 text-right text-[11px] ${s.key === "live" ? "text-blue" : "text-dim"}`}>{when}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </StepCard>
  );
}

/* -------------------------------------------------------------- 2. market */

function MarketStep({ markets, note, onPick }: { markets: MmMarket[]; note: string | null; onPick: (m: MmMarket) => void }) {
  const [text, setText] = useState("");
  const shown = text.trim() ? markets.filter((m) => matches(m.label, text)) : markets;
  return (
    <StepCard title="Market" right={<span className="nums text-[11px] text-faint">{markets.length}</span>}>
      {note && <Note>{note}. Pick a market.</Note>}
      {markets.length > FILTER_OVER && <FilterInput value={text} onChange={setText} placeholder="Filter markets" label="Filter markets" />}
      {shown.length === 0 ? (
        <div className="py-3 text-center text-[13px] text-dim">No market matches “{text.trim()}”</div>
      ) : (
        <div className={LIST}>
          {shown.map((m) => (
            <button key={m.marketKey} type="button" onClick={() => onPick(m)} className={ROW}>
              <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{m.label}</span>
              <span className="nums shrink-0 text-[11px] text-dim">{marketHint(m)}</span>
              <span className="shrink-0 text-faint" aria-hidden>
                ›
              </span>
            </button>
          ))}
        </div>
      )}
    </StepCard>
  );
}

/* --------------------------------------------------------- 3. participant */

function ParticipantStep({
  market,
  participants,
  note,
  onPick,
}: {
  market: MmMarket;
  participants: MmParticipant[];
  note: string | null;
  onPick: (p: MmParticipant) => void;
}) {
  const [text, setText] = useState("");
  const outcomes = market.books.every((b) => b.line == null || b.line === 0);
  const noun = outcomes ? "outcome" : "player";
  const shown = text.trim() ? participants.filter((p) => matches(p.name, text)) : participants;
  return (
    <StepCard title={outcomes ? "Outcome" : "Player"} right={<span className="nums text-[11px] text-faint">{participants.length} · most venues first</span>}>
      {note && <Note>{note}. Pick a {noun}.</Note>}
      {participants.length > FILTER_OVER && <FilterInput value={text} onChange={setText} placeholder={`Filter ${noun}s`} label={`Filter ${noun}s`} />}
      {shown.length === 0 ? (
        <div className="py-3 text-center text-[13px] text-dim">No {noun} matches “{text.trim()}”</div>
      ) : (
        <div className={LIST}>
          {shown.map((p) => (
            <button key={p.id} type="button" onClick={() => onPick(p)} className={ROW}>
              <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{p.name}</span>
              {p.books.length > 1 && <span className="nums shrink-0 text-[11px] text-dim">{p.books.length} lines</span>}
              <span className="nums w-[4.5rem] shrink-0 text-right text-[11px] text-faint">
                {p.venues} venue{p.venues === 1 ? "" : "s"}
              </span>
              <span className="shrink-0 text-faint" aria-hidden>
                ›
              </span>
            </button>
          ))}
        </div>
      )}
    </StepCard>
  );
}

/* ---------------------------------------------------------------- 4. line */

function LineStep({ lines, note, onPick }: { lines: MmLineOption[]; note: string | null; onPick: (l: MmLineOption) => void }) {
  const { format } = useOddsFormat();
  return (
    <StepCard title="Line" right={<span className="nums text-[11px] text-faint">{lines.length}</span>}>
      {note && <Note>{note}. Pick another line.</Note>}
      <div className={LIST}>
        {lines.map((l) => (
          <button key={l.book.key} type="button" onClick={() => onPick(l)} className={ROW}>
            <span className="flex min-w-0 flex-1 items-center gap-1.5">
              <span className="nums min-w-0 truncate text-[13px] text-ink">{l.label}</span>
              {l.main && <span className="shrink-0 rounded-full bg-amber/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber">main</span>}
            </span>
            {l.sides.map((s) => (
              <span key={s.label} className="flex w-[4.75rem] shrink-0 flex-col items-end leading-tight">
                <span className="max-w-full truncate text-[10px] text-faint" title={s.label}>
                  {shortSide(s.label)}
                </span>
                <span className="nums text-[13px] font-medium text-ink">{fmtPrice(s.price, s.american, format)}</span>
              </span>
            ))}
          </button>
        ))}
      </div>
    </StepCard>
  );
}

/* ============================================================ MarketPicker */

export interface PickedMarket {
  contestId: string;
  contest: Contest | undefined;
  market: MmMarket;
  book: MmBook;
}

/**
 * The picker on its own: title + ✕, the breadcrumb and the
 * current step. Picking the last step (or a game/market with only one line) calls `onPick` once.
 */
export function MarketPicker({
  source,
  title = "Add market",
  onPick,
  onClose,
}: {
  source: PickerSource;
  title?: string;
  onPick: (p: PickedMarket) => void;
  onClose: () => void;
}) {
  const state = useMarketPick(source, () => EMPTY_PICK);
  const { flow, pick, contest } = state;
  const sent = useRef<string | null>(null);
  useEffect(() => {
    if (flow.step !== "quote" || !flow.market || !flow.book || !pick.contestId) return;
    const k = `${pick.contestId}|${flow.market.marketKey}|${flow.book.key}`;
    if (sent.current === k) return;
    sent.current = k;
    onPick({ contestId: pick.contestId, contest, market: flow.market, book: flow.book });
  }, [flow, pick.contestId, contest, onPick]);
  const crumbs = pickCrumbs(state, contest ? gameLabel(contest) : "");
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <h2 className="text-[14px] font-semibold text-ink">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          title="Close"
          className="ml-auto inline-flex min-h-11 w-11 items-center justify-center rounded-full border border-border-2 bg-panel text-[12px] text-dim hover:border-amber/40 hover:text-ink lg:min-h-8 lg:w-8"
        >
          ✕
        </button>
      </div>
      <PickerCrumbs crumbs={crumbs} kickoff={contest?.starts_at ?? null} label="Market steps" />
      <PickerSteps source={source} state={state} />
    </div>
  );
}
